import {AnimatePresence, m as motion} from 'framer-motion'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import Button from '@/components/button'
import {Modal, ModalCloseButton} from '@/components/modal'
import {Text} from '@/components/text'
import type {InvoiceGeneratorFragment} from '@/graphql-types'
import {useDateFormatter} from '@/hooks/use-date-formatter'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'
import {Section} from '@/styles/grid'
import {colors, tokens} from '@/theme'
import currencyFormatter from '@/utils/currency-formatter'
import mq from '@/utils/media-query'

import type {State} from './state'

interface Props extends State {
    content: InvoiceGeneratorFragment['data']
    isOpen: boolean
    className?: string
    onClosePreview: () => void
    onGetYourInvoice?: () => void
}
export const Preview = ({
    isOpen,
    className,
    content,
    onClosePreview,
    onGetYourInvoice,
    ...state
}: Props) => {
    const globalStrings = useGlobalStrings()
    const currentLocale = useLocale()
    const formatCurrency = currencyFormatter(
        currentLocale.code,
        state.currency,
        {
            maximumFractionDigits: 2,
        },
        true,
    )
    const taxAmount = state.subtotal * (state.tax / 100)
    const formatDate = useDateFormatter()

    return (
        <>
            {isOpen && <PageStyles />}

            <FullScreenModal
                isOpen={isOpen}
                naked={true}
                overlayBgColor="pink"
                className={className}
            >
                <StyledCloseButton size="32px" onClick={onClosePreview} />

                <AnimatePresence>
                    {isOpen && (
                        <StyledSection
                            forwardedAs={motion.div}
                            padding="small"
                            initial={{opacity: 0, translateY: '75%'}}
                            animate={{
                                opacity: 1,
                                translateY: '0%',
                                transition: {duration: 0.23, type: 'ease'},
                            }}
                        >
                            <Content>
                                <Header>
                                    <Text $variant="heading-base">{content.invoice_title}</Text>
                                    {state.yourCompanyLogo && (
                                        <CompanyLogo src={state.yourCompanyLogo} alt="" />
                                    )}
                                </Header>

                                <TopSection>
                                    <List>
                                        <li>{state.yourCompanyName}</li>
                                        <li>{state.yourEmail}</li>
                                        <li>{state.yourAddress}</li>
                                        <li>{state.yourCity}</li>
                                        <li>{state.yourZipCode}</li>
                                        <li>{state.yourCountry}</li>
                                    </List>

                                    <DescriptionList>
                                        <dt>{globalStrings.invoice_number}</dt>
                                        <dd>{state.invoiceNumber}</dd>

                                        <dt>{globalStrings.issued_on}</dt>
                                        {state.invoiceIssueDate && (
                                            <dd>{formatDate(state.invoiceIssueDate.toString())}</dd>
                                        )}

                                        <dt>{globalStrings.due_date}</dt>
                                        {state.invoiceDueDate && (
                                            <dd>{formatDate(state.invoiceDueDate.toString())}</dd>
                                        )}
                                    </DescriptionList>

                                    <List>
                                        <li>{state.recipientsCompanyName}</li>
                                        <li>{state.recipientsEmail}</li>
                                        <li>{state.recipientsAddress}</li>
                                        <li>{state.recipientsCity}</li>
                                        <li>{state.recipientsZipCode}</li>
                                        <li>{state.recipientsCountry}</li>
                                    </List>
                                </TopSection>

                                {state.notes && <Notes>{state.notes}</Notes>}

                                <div>
                                    <StyledTable>
                                        <thead>
                                            <tr>
                                                <th>{content.items_label}</th>
                                                <th>{content.quantity_label}</th>
                                                <th>{content.rate_label}</th>
                                                <th>{content.price_label}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.invoiceItems.map((item) => (
                                                <tr key={item.id}>
                                                    <td>{item.name}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{formatCurrency(item.price)}</td>
                                                    <td>
                                                        {formatCurrency(item.price * item.quantity)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </StyledTable>
                                </div>

                                <div>
                                    <Totals>
                                        <dt>{content.subtotal_label}</dt>
                                        <dt style={{textAlign: 'right'}}>
                                            {formatCurrency(state.subtotal)}
                                        </dt>
                                        <dt>{content.tax_label}</dt>
                                        <dt style={{textAlign: 'right'}}>
                                            {formatCurrency(taxAmount)}
                                        </dt>
                                        <dt>
                                            <strong>{content.total_label}</strong>
                                        </dt>
                                        <dt style={{textAlign: 'right'}}>
                                            <strong>
                                                {formatCurrency(state.subtotal + taxAmount)}
                                            </strong>
                                        </dt>
                                    </Totals>
                                </div>

                                {state.terms && <Terms>{state.terms}</Terms>}
                            </Content>

                            {onGetYourInvoice && (
                                <StyledButton
                                    onClick={() => {
                                        onGetYourInvoice()
                                        onClosePreview()
                                    }}
                                >
                                    {content.get_invoice_label}
                                </StyledButton>
                            )}
                        </StyledSection>
                    )}
                </AnimatePresence>
            </FullScreenModal>
        </>
    )
}

const PageStyles = createGlobalStyle`
    html,
    body {
        font-size: 18px;

        @media print {
            font-size: 14px;
            background: ${colors.background.default} !important;
        }
    }

    @media print {
        header, main, footer {
            display: none !important;
        }
    }

    @page {
        size: a4 portrait;

        /* this affects the margin in the printer settings */
        margin: ${tokens.spacing60};
    }
`

const FullScreenModal = styled(Modal)`
    padding: 0;

    @media print {
        background: ${colors.background.default} !important;
    }

    & > div[role='dialog'] {
        max-width: none;
        height: 100%;
        background-color: transparent;
    }
`

const StyledCloseButton = styled(ModalCloseButton)`
    padding-bottom: 0;

    @media print {
        display: none !important;
    }
`
const StyledSection = styled(Section)`
    @media print {
        padding: 0;
    }
`

const Content = styled.div`
    position: relative;
    display: grid;
    row-gap: ${tokens.spacing24};
    width: 100%;
    max-width: 986px;
    background-color: ${colors.background.default};
    padding: ${tokens.spacing60};
    margin-left: auto;
    margin-right: auto;

    thead tr th {
        padding-bottom: ${tokens.spacing32};
    }

    ${mq.min('sm')} {
        row-gap: ${tokens.spacing28};
    }

    /* The break-inside property doesn't work with grid layouts, so we make it block and use margin instead of gap */
    @media print {
        display: block;
        max-width: unset;
        padding: 0;

        > * + * {
            margin-top: ${tokens.spacing28};
        }
    }

    @media screen {
        ${mq.max('sm')} {
            li,
            dt,
            dd,
            thead,
            tbody,
            div {
                font-size: ${tokens.fontSmall};
            }

            p {
                font-size: ${tokens.font3XLarge};
            }

            padding: ${tokens.spacing32};
        }
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
`

const CompanyLogo = styled.img`
    width: 100px;
    height: 100px;
    object-fit: contain;
    object-position: left bottom;

    @media screen {
        ${mq.max('sm')} {
            width: 50px;
            height: 50px;
        }
    }
`

const TopSection = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: ${tokens.spacing10};
`

const List = styled.ul`
    list-style-type: none;
    padding-left: 0;
    margin: 0;
`

const DescriptionList = styled.dl`
    dt {
        font-weight: 600;
    }
`

const StyledTable = styled.table`
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: separate;

    th,
    td {
        font-weight: normal;
        text-align: right;
        padding: ${tokens.spacing6} ${tokens.spacing6};

        &:first-child {
            padding-left: 0;
            width: 50%;
            text-align: left;
        }

        &:last-child {
            padding-right: 0;
        }
    }

    @media screen {
        ${mq.max('sm')} {
            th {
                padding-bottom: ${tokens.spacing20};
            }

            td {
                padding: ${tokens.spacing2} ${tokens.spacing6};
            }
        }
    }
`

const Totals = styled.dl`
    display: grid;
    grid-template-columns: auto max-content;
    gap: ${tokens.spacing10};

    @media screen {
        ${mq.max('sm')} {
            gap: ${tokens.spacing6};
        }
    }
`

const StyledButton = styled(Button)`
    display: block;
    margin: ${tokens.spacing40} auto 0;

    @media print {
        display: none;
    }
`

const Notes = styled.div`
    margin: 100px 0;

    @media screen {
        ${mq.max('sm')} {
            margin: ${tokens.spacing28} 0;
        }
    }
`

const Terms = styled.div`
    margin-top: ${tokens.spacing40};

    @media screen {
        ${mq.max('sm')} {
            margin-top: ${tokens.spacing12};
        }
    }
`
